import { CheckCircle, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import BgImage from "../../../assets/bg1.png";
import { FileImage, FileVideo, File } from "lucide-react";
import axios from "axios";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AutohideSnackbar from "../../../components/AutohideSnackbar";
import Logo from "../../../assets/speech-analyzer-logo.png";
import DemoAudio from "../../../assets/demoAudio.mp3";
import DemoAudioFile from "./DemoAudioFile";
import axiosInstance from "../../../services/axiosInterceptors";
import { palette, theme as colorTheme} from "../../../Convoze.theme";
import ParentWrapper from "../../../components/ParentWrapper";

const primary = colorTheme('palette', 'primary').value;
const pop = colorTheme('palette', 'pop').value;
const positive = colorTheme('palette', 'positive').value;
const neutral = colorTheme('palette', 'neutral').value;
const negative = colorTheme('palette', 'negative').value;



const allowedExtensions = [
  ".wav",
  ".mp3",
  ".m4a",
  ".aac",
  ".flac",
  ".ogg",
  ".wma",
  ".aiff",
  ".alac",
  ".opus",
];

// Styled components
const UploadBox = styled(Box)(({ theme, isDragging }) => ({
  border: "2px dashed rgba(99, 102, 241, 0.3)",
  borderRadius: 16,
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(99, 102, 241, 0.05)" : "white",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  minHeight: 100,
  "&:hover": {
    borderColor: primary,
    backgroundColor: "rgba(99, 102, 241, 0.05)",
  },
}));
const Root = styled("div")(({ theme }) => ({
  width: "100%",
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  "& > :not(style) ~ :not(style)": {
    marginTop: theme.spacing(2),
  },
}));

const IconGroup = styled(Box)({
  display: "flex",
  gap: "4px",
  marginBottom: "24px",
});

const UploadButton = styled(Button)({
  backgroundColor: primary,
  borderRadius: "24px",
  padding: "10px 32px",
  textTransform: "none",
  fontSize: "16px",
  "&:hover": {
    backgroundColor: primary,
  },
});

const FileUpload = () => {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [disableDefaultAudio, setDisableDefaultAudio] = useState(false);
  const [disable, setDisable] = useState(false);
  const token = localStorage.getItem("token");

  //snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Function to validate allowed files
  const validateFiles = (files) => {
    const validFiles = files.filter((file) =>
      allowedExtensions.includes(file?.name.slice(file?.name.lastIndexOf(".")))
    );

    if (validFiles.length !== files.length) {
      alert(
        "Some files have unsupported formats. Only .wav, .mp3, .m4a, .aac, .flac, .ogg, .wma, .aiff, .alac, and .opus files are allowed."
      );
    }

    return validFiles;
  };

  // Function to upload files
  const uploadFiles = async (files, language) => {
    setDisable(true);
    if (!files || files.length === 0) {
      console.error("No files selected for upload");
      return;
    }
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    if (language) {
      formData.append("language", language);
    }
    try {
      const response = await axiosInstance.post(
        `${apiEndPointsConfig?.demoUploadFile}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.status?.code == 200) {
        navigate(`/demo/audio/transcript/${response?.data?.data[0]?.id}`);
      }
      setDisable(false);
    } catch (error) {
      setDisable(false);
      setDisableDefaultAudio(false);
      setSnackbarMessage(error?.response?.data?.status?.value);
      setSnackbarOpen(true);
      return error;
    }
  };

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      files: [],
      language: "",
    },
    onSubmit: (data) => {
      uploadFiles(data?.files, data?.language);
    },
  });

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);
    formik.setFieldValue("files", validFiles);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = validateFiles(files);
    formik.setFieldValue("files", validFiles);
    setDisableDefaultAudio(true);
  };
  return (
   <ParentWrapper>
     <Box
      sx={{
        backgroundImage:
          "url('https://my.productfruits.com/new-web-dist/assets/onBoardingBackground.87827ee6.svg')",
        backgroundSize: "contained", // Ensures the image covers the entire box
        backgroundPosition: "right center", // Centers the image
        backgroundRepeat: "no-repeat", // Prevents repetition
        width: "100%", // Ensures it takes full width
        minHeight: "100vh", // Ensures full height
      }}
    >
      <Grid container>
        <Box
          sx={{
            p: 4,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              p: 8,
              borderRadius: 3,
              border: "1px solid #e3e3e3",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <img
                src={
                  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGkAAABpCAYAAAA5gg06AAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAU1SURBVHgB7Z3NbhtVFMf/d+xILM2GbV0k1iRSQSA1rbNgTd8A9wFQ0hcgyRNAFqzj7JHaPkGmHwgokZIHQKr7BEQs8cflnGs7SvyViT1z5wz6/yQ7djxdND+fc8/9mHsBYh4HS5z6BurYxRBtWMGhO351IY8P8v4CfXnsuEtEwpak1/5YntuoAl5EOaTyeIltl6JA7EjSKKrhb1STrvwlOxJhJxJhXeSMJUlNkfQe1aeDAQ7zlJWA5E07fNk0desXLwcoqThU1jne+H2sCdNdHLqSAndWTYGMpDg0Q1S99m2sACXFoyGP41XSHyXFxuPgrqIoqQxU1KjjnglKKo921oiipDLJmPooqWxU1KlvLbuEkixQw/NloxOUZAMdXF5YSFCSHVpS8e3N+4CSbLEfpmymoCRbNMTITDRRkjUcdqejiZLsMRNNlGSRqWiiJJtoSd6evKEku3w7eUFJdmlNUh4lWaaGJ/qDkmzzWJ8oyTYtfaIk2zS1XaIk+zQpyTo1bFKSdTwjyT4O9yipAlBSBagjEuct3+j9i6ZzUvsnuKe51nts6mfyu6b7Ad0Hb1ypNxB887Pf/Ocj7P/x2ainb4VC/ygqpt+T0VwXBgtVSGPRtaJHJd2HAe7/4k/ffzLqSBqgU0gknW37JxIlu/2+/Edt3ZWbCRF0BJiRlF+6C1HTl8kqYE8ENVBlHC4lHdvA40Mukt5t+3avh31tW0DyRZqBtST99pVv1us4FtuVTGuVYIDuyiX4u4d+VwSdw1Du/p9ycedI0rZn0MOPviqbYlQZP9p55U6SNL1J23PKticSLmyVk33EQQXVahQUmVf6lEkSBZXEAKn+uFUSBZVGOtn3YakkLRIoqDROJi+WStIqjoJKwKOLR64zebuwutN+EMvsknCjtmjC3EjSdkgGEH4CKQfdiu0acyXJSMJzkHLws3vlzUjSwVKM5n5IbLQtGqIz/evZSBpi7f3ZyIq4+TtO3pCkUcRqrjQ61yu669yMJEZROfiwaeHhoo+vJDGKSsTh2bJdJa8kOY/vQOKj1dwj92LZJUGS9ovAybv4qKDH7uC2y4Kk2gYFRcfjKIsgJUhiqovMKIL2sl4+aZNaIHHImOKuU//z4fIN8UhOeFxKFfdUBL246z9NBp5DQBFIpQ+6dVsVt4h6wr5RcYyiR0vstWYUxBE+BymCzjh61p7y0Um/aq/btocezXMkIwgXyAmt7powgPdo6poKVBEfFvgfipyPJXKe5ilIMRVJ/UEY4H2GapCKmFeS0nRVT4oCiXanXyY89s62fWMo6WIwRLSDDaf5/i/g7NOrAxcvw+GLPhy6qKPVGiVxD16UfpKVO3EskX7x1u3ACLyxuQIk3l+FNTFK4lx5uZ9kQ9NdF8Q0idejoolpksmNSsQuyaB3c90xsUfy9e9hlQqLB8OEfpKU4S9BzJKMn1MQswRJGzXojCFTnlGCpK00DBamICa5PnZ3BGKSK0ky6puC0WSS6VHwQxBz3JDEaLLJvPkkRpMxZiRpNDkWEaaYOzNbq+OAk4F2mCtJ+01O1y0TEyxc4zAuItg+GWDpQhQRpWnvBKRUbl0ttLERDqblxGCJ3CpJ26d6HboGjaJKItO6O4oql8yLI1WUtFFb7EPF584rWB+8DTfksuqLyErLjLXq6/dxnx3eOKy8FlwXsEjltwVGVeGstWB/3E5Noor9qYLI5a4Kjaovf3VtyiqGXG99uSFLqsCqtll6KhoMUfiBOrqZxxBoJaPD1lswjpMvVm+AnfGiURP8BwDIlSd/wd8IAAAAAElFTkSuQmCC"
                }
                style={{ height: 22 }}
                alt="Logo"
              />
              <Typography fontWeight={550}>
                &nbsp;&nbsp;Speech Analytics
              </Typography>
            </Box>

            <Typography color="grey">1 / 2</Typography>

            <Typography
              variant="h6"
              sx={{
                color: primary,
                mt: 1,
              }}
            >
              Upload Recording
            </Typography>
            <Typography color="grey" variant="body2" mt={0}>
              Audio file to view the transcript.
            </Typography>
            {/* <CustomizedSteppers activeTab={activeTab} /> */}
            <Grid item md={12} xs={12}>
              <Box sx={{ width: "100%", mx: "auto", mt: 4 }}>
                <form onSubmit={formik.handleSubmit}>
                  <UploadBox
                    isDragging={isDragging}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload files"
                  >
                    <IconGroup>
                      <FileImage size={32} color={primary} />
                      <File size={32} color={primary} />
                      <FileVideo size={32} color={primary} />
                    </IconGroup>

                    <Typography variant="h5" component="div" sx={{ mb: 1 }}>
                      Drag & drop{" "}
                      <Box component="span" sx={{ color:{primary} }}>
                        audio&nbsp;
                      </Box>
                      <Box component="span" sx={{ color:{primary} }}>
                        files
                      </Box>
                      , or any{" "}
                      <Box component="span" sx={{ color: {primary} }}>
                        file
                      </Box>
                    </Typography>

                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      or{" "}
                      <Box
                        component="span"
                        sx={{
                          color:primary,
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        browse files
                      </Box>{" "}
                      on your computer
                    </Typography>

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInput}
                      style={{ display: "none" }}
                      multiple
                    />
                  </UploadBox>

                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="audio-language-label">Audio Language</InputLabel>
                    <Select
                      labelId="audio-language-label"
                      id="audio-language"
                      value={formik.values.language}
                      label="Audio Language"
                      onChange={(e) => formik.setFieldValue("language", e.target.value)}
                    >
                      <MenuItem value="hi-IN">Hindi</MenuItem>
                      <MenuItem value="ur-IN">Urdu</MenuItem>
                      <MenuItem value="gu-IN">Gujarati</MenuItem>
                      <MenuItem value="bn-IN">Bengali</MenuItem>
                      <MenuItem value="ml-IN">Malayalam</MenuItem>
                      <MenuItem value="ta-IN">Tamil</MenuItem>
                      <MenuItem value="te-IN">Telugu</MenuItem>
                      <MenuItem value="mr-IN">Marathi</MenuItem>
                      <MenuItem value="kn-IN">Kannada</MenuItem>
                      <MenuItem value="pa-IN">Punjabi</MenuItem>
                      <MenuItem value="en-IN">English (Indian)</MenuItem>
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    {disable == false ? (
                      <UploadButton
                        type="submit"
                        variant="contained"
                        disabled={formik.values.files.length === 0}
                        sx={{
                          bgcolor: primary,
                          color: "white",
                          textTransform: "none",
                          fontSize: "1rem",
                          height: "40px",
                          mb: 3,
                          borderRadius: "12px",
                          "&:hover": {
                            bgcolor: primary,
                          },
                          mt: 2,
                        }}
                      >
                        Upload
                      </UploadButton>
                    ) : (
                      <UploadButton
                        type="submit"
                        variant="contained"
                        disabled={true}
                        sx={{
                          bgcolor: primary,
                          color: "white",
                          textTransform: "none",
                          fontSize: "1rem",
                          height: "40px",
                          mb: 3,
                          borderRadius: "12px",
                          "&:hover": {
                            bgcolor: primary,
                          },
                          mt: 2,
                        }}
                      >
                        Uploading &nbsp;&nbsp;
                        <CircularProgress size={20} />
                      </UploadButton>
                    )}
                  </Box>
                </form>
              </Box>
            </Grid>
            <Root>
              <Divider sx={{ mb: 2 }}>OR</Divider>
            </Root>

            <DemoAudioFile
              disable={disable}
              disableDefaultAudio={disableDefaultAudio}
              setDisableDefaultAudio={setDisableDefaultAudio}
            />
          </Card>
        </Box>
        <AutohideSnackbar
          message={snackbarMessage}
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
        />
      </Grid>
    </Box>
   </ParentWrapper>
  );
};

export default FileUpload;
